import React, { useState, useEffect } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/App";
import { Calculator, TrendingUp, Target, Wallet, PiggyBank, IndianRupee } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

export const CalculatorsPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="calculators-page">
      <Navbar />
      
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Investment <span className="gold-text">Calculators</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Plan your investments with precision using our smart calculators
            </p>
          </div>

          {/* Calculator Tabs */}
          <Tabs defaultValue="sip" className="space-y-8">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-transparent h-auto p-0">
              <TabsTrigger 
                value="sip" 
                className="flex items-center gap-2 px-6 py-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl bg-card border border-border"
                data-testid="sip-tab"
              >
                <PiggyBank className="w-5 h-5" />
                SIP
              </TabsTrigger>
              <TabsTrigger 
                value="lumpsum"
                className="flex items-center gap-2 px-6 py-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl bg-card border border-border"
                data-testid="lumpsum-tab"
              >
                <Wallet className="w-5 h-5" />
                Lumpsum
              </TabsTrigger>
              <TabsTrigger 
                value="swp"
                className="flex items-center gap-2 px-6 py-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl bg-card border border-border"
                data-testid="swp-tab"
              >
                <TrendingUp className="w-5 h-5" />
                SWP
              </TabsTrigger>
              <TabsTrigger 
                value="goal"
                className="flex items-center gap-2 px-6 py-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl bg-card border border-border"
                data-testid="goal-tab"
              >
                <Target className="w-5 h-5" />
                Goal Planning
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sip">
              <SIPCalculator />
            </TabsContent>
            <TabsContent value="lumpsum">
              <LumpsumCalculator />
            </TabsContent>
            <TabsContent value="swp">
              <SWPCalculator />
            </TabsContent>
            <TabsContent value="goal">
              <GoalCalculator />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// SIP Calculator Component
const SIPCalculator = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(10000);
  const [years, setYears] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [result, setResult] = useState(null);

  const calculate = async () => {
    try {
      const response = await axios.post(`${API}/calculate/sip`, {
        monthly_investment: monthlyInvestment,
        years: years,
        expected_return: expectedReturn
      });
      setResult(response.data);
    } catch (error) {
      // Calculate locally if API fails
      const rate = expectedReturn / 100 / 12;
      const months = years * 12;
      const futureValue = monthlyInvestment * (((1 + rate) ** months - 1) / rate) * (1 + rate);
      const totalInvested = monthlyInvestment * months;
      setResult({
        future_value: futureValue,
        total_invested: totalInvested,
        total_returns: futureValue - totalInvested
      });
    }
  };

  useEffect(() => {
    calculate();
  }, [monthlyInvestment, years, expectedReturn]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const chartData = result ? [
    { name: "Invested", value: result.total_invested, fill: "hsl(var(--chart-3))" },
    { name: "Returns", value: result.total_returns, fill: "hsl(var(--chart-1))" }
  ] : [];

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="sip-calculator">
      {/* Input Section */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <PiggyBank className="w-6 h-6 text-primary" />
            SIP Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Monthly Investment</Label>
              <div className="flex items-center gap-1 bg-secondary rounded-lg px-3 py-1">
                <IndianRupee className="w-4 h-4" />
                <Input
                  type="number"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-24 bg-transparent border-0 p-0 text-right font-mono"
                  data-testid="sip-monthly-input"
                />
              </div>
            </div>
            <Slider
              value={[monthlyInvestment]}
              onValueChange={([v]) => setMonthlyInvestment(v)}
              min={500}
              max={100000}
              step={500}
              className="[&_[role=slider]]:bg-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>₹500</span>
              <span>₹1,00,000</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Investment Period</Label>
              <span className="bg-secondary rounded-lg px-3 py-1 font-mono">{years} years</span>
            </div>
            <Slider
              value={[years]}
              onValueChange={([v]) => setYears(v)}
              min={1}
              max={30}
              step={1}
              className="[&_[role=slider]]:bg-primary"
              data-testid="sip-years-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 year</span>
              <span>30 years</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Expected Return (p.a.)</Label>
              <span className="bg-secondary rounded-lg px-3 py-1 font-mono">{expectedReturn}%</span>
            </div>
            <Slider
              value={[expectedReturn]}
              onValueChange={([v]) => setExpectedReturn(v)}
              min={1}
              max={30}
              step={0.5}
              className="[&_[role=slider]]:bg-primary"
              data-testid="sip-return-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1%</span>
              <span>30%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Result Section */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl">Investment Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {result && (
            <>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => formatCurrency(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                  <span className="text-muted-foreground">Invested Amount</span>
                  <span className="font-mono text-lg font-semibold" data-testid="sip-invested-amount">
                    {formatCurrency(result.total_invested)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                  <span className="text-muted-foreground">Est. Returns</span>
                  <span className="font-mono text-lg font-semibold text-green-500" data-testid="sip-returns">
                    +{formatCurrency(result.total_returns)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-primary/10 rounded-xl border border-primary/20">
                  <span className="font-medium">Total Value</span>
                  <span className="font-mono text-2xl font-bold text-primary" data-testid="sip-total-value">
                    {formatCurrency(result.future_value)}
                  </span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Lumpsum Calculator Component
const LumpsumCalculator = () => {
  const [investment, setInvestment] = useState(100000);
  const [years, setYears] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [result, setResult] = useState(null);

  const calculate = async () => {
    try {
      const response = await axios.post(`${API}/calculate/lumpsum`, {
        investment: investment,
        years: years,
        expected_return: expectedReturn
      });
      setResult(response.data);
    } catch (error) {
      const rate = expectedReturn / 100;
      const futureValue = investment * Math.pow(1 + rate, years);
      setResult({
        future_value: futureValue,
        total_invested: investment,
        total_returns: futureValue - investment
      });
    }
  };

  useEffect(() => {
    calculate();
  }, [investment, years, expectedReturn]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Generate growth chart data
  const growthData = [];
  for (let i = 0; i <= years; i++) {
    growthData.push({
      year: i,
      value: investment * Math.pow(1 + expectedReturn / 100, i)
    });
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="lumpsum-calculator">
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Wallet className="w-6 h-6 text-primary" />
            Lumpsum Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Investment Amount</Label>
              <div className="flex items-center gap-1 bg-secondary rounded-lg px-3 py-1">
                <IndianRupee className="w-4 h-4" />
                <Input
                  type="number"
                  value={investment}
                  onChange={(e) => setInvestment(Number(e.target.value))}
                  className="w-28 bg-transparent border-0 p-0 text-right font-mono"
                  data-testid="lumpsum-amount-input"
                />
              </div>
            </div>
            <Slider
              value={[investment]}
              onValueChange={([v]) => setInvestment(v)}
              min={10000}
              max={10000000}
              step={10000}
              className="[&_[role=slider]]:bg-primary"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Investment Period</Label>
              <span className="bg-secondary rounded-lg px-3 py-1 font-mono">{years} years</span>
            </div>
            <Slider
              value={[years]}
              onValueChange={([v]) => setYears(v)}
              min={1}
              max={30}
              step={1}
              className="[&_[role=slider]]:bg-primary"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Expected Return (p.a.)</Label>
              <span className="bg-secondary rounded-lg px-3 py-1 font-mono">{expectedReturn}%</span>
            </div>
            <Slider
              value={[expectedReturn]}
              onValueChange={([v]) => setExpectedReturn(v)}
              min={1}
              max={30}
              step={0.5}
              className="[&_[role=slider]]:bg-primary"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl">Growth Projection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growthData}>
                <defs>
                  <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                <Tooltip 
                  contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                  formatter={(value) => [formatCurrency(value), 'Value']}
                />
                <Area type="monotone" dataKey="value" stroke="hsl(var(--chart-1))" strokeWidth={2} fillOpacity={1} fill="url(#colorGrowth)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {result && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                <span className="text-muted-foreground">Invested Amount</span>
                <span className="font-mono text-lg font-semibold">{formatCurrency(result.total_invested)}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                <span className="text-muted-foreground">Est. Returns</span>
                <span className="font-mono text-lg font-semibold text-green-500">+{formatCurrency(result.total_returns)}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-xl border border-primary/20">
                <span className="font-medium">Total Value</span>
                <span className="font-mono text-2xl font-bold text-primary" data-testid="lumpsum-total-value">
                  {formatCurrency(result.future_value)}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// SWP Calculator Component
const SWPCalculator = () => {
  const [corpus, setCorpus] = useState(1000000);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(10000);
  const [expectedReturn, setExpectedReturn] = useState(8);
  const [result, setResult] = useState(null);

  const calculate = async () => {
    try {
      const response = await axios.post(`${API}/calculate/swp`, {
        corpus: corpus,
        monthly_withdrawal: monthlyWithdrawal,
        expected_return: expectedReturn
      });
      setResult(response.data);
    } catch (error) {
      // Local calculation
      const rate = expectedReturn / 100 / 12;
      let balance = corpus;
      let months = 0;
      while (balance > 0 && months < 600) {
        balance = balance * (1 + rate) - monthlyWithdrawal;
        months++;
      }
      setResult({
        months_sustainable: months < 600 ? months : "50+ years",
        total_withdrawn: Math.min(months, 600) * monthlyWithdrawal,
        initial_corpus: corpus,
        monthly_withdrawal: monthlyWithdrawal
      });
    }
  };

  useEffect(() => {
    calculate();
  }, [corpus, monthlyWithdrawal, expectedReturn]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="swp-calculator">
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-primary" />
            SWP Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Total Corpus</Label>
              <div className="flex items-center gap-1 bg-secondary rounded-lg px-3 py-1">
                <IndianRupee className="w-4 h-4" />
                <Input
                  type="number"
                  value={corpus}
                  onChange={(e) => setCorpus(Number(e.target.value))}
                  className="w-28 bg-transparent border-0 p-0 text-right font-mono"
                  data-testid="swp-corpus-input"
                />
              </div>
            </div>
            <Slider
              value={[corpus]}
              onValueChange={([v]) => setCorpus(v)}
              min={100000}
              max={50000000}
              step={100000}
              className="[&_[role=slider]]:bg-primary"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Monthly Withdrawal</Label>
              <div className="flex items-center gap-1 bg-secondary rounded-lg px-3 py-1">
                <IndianRupee className="w-4 h-4" />
                <Input
                  type="number"
                  value={monthlyWithdrawal}
                  onChange={(e) => setMonthlyWithdrawal(Number(e.target.value))}
                  className="w-24 bg-transparent border-0 p-0 text-right font-mono"
                  data-testid="swp-withdrawal-input"
                />
              </div>
            </div>
            <Slider
              value={[monthlyWithdrawal]}
              onValueChange={([v]) => setMonthlyWithdrawal(v)}
              min={1000}
              max={500000}
              step={1000}
              className="[&_[role=slider]]:bg-primary"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Expected Return (p.a.)</Label>
              <span className="bg-secondary rounded-lg px-3 py-1 font-mono">{expectedReturn}%</span>
            </div>
            <Slider
              value={[expectedReturn]}
              onValueChange={([v]) => setExpectedReturn(v)}
              min={1}
              max={20}
              step={0.5}
              className="[&_[role=slider]]:bg-primary"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl">Withdrawal Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {result && (
            <div className="space-y-4">
              <div className="text-center p-8 bg-primary/10 rounded-xl border border-primary/20">
                <p className="text-muted-foreground mb-2">Your corpus will last</p>
                <p className="font-mono text-4xl font-bold text-primary" data-testid="swp-duration">
                  {typeof result.months_sustainable === 'number' 
                    ? `${Math.floor(result.months_sustainable / 12)} years ${result.months_sustainable % 12} months`
                    : result.months_sustainable}
                </p>
              </div>

              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                <span className="text-muted-foreground">Initial Corpus</span>
                <span className="font-mono text-lg font-semibold">{formatCurrency(result.initial_corpus)}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                <span className="text-muted-foreground">Monthly Withdrawal</span>
                <span className="font-mono text-lg font-semibold">{formatCurrency(result.monthly_withdrawal)}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                <span className="text-muted-foreground">Total Withdrawn</span>
                <span className="font-mono text-lg font-semibold text-green-500">{formatCurrency(result.total_withdrawn)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Goal Planning Calculator
const GoalCalculator = () => {
  const [goalAmount, setGoalAmount] = useState(1000000);
  const [years, setYears] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [initialLumpsum, setInitialLumpsum] = useState(0);
  const [result, setResult] = useState(null);

  const calculate = async () => {
    try {
      const response = await axios.post(`${API}/calculate/goal`, {
        goal_amount: goalAmount,
        years: years,
        expected_return: expectedReturn,
        initial_lumpsum: initialLumpsum
      });
      setResult(response.data);
    } catch (error) {
      const rate = expectedReturn / 100 / 12;
      const months = years * 12;
      const annualRate = expectedReturn / 100;
      
      // Calculate lumpsum growth
      const lumpsumGrowth = initialLumpsum * Math.pow(1 + annualRate, years);
      
      // Remaining amount needed via SIP
      const remainingGoal = Math.max(0, goalAmount - lumpsumGrowth);
      
      // Monthly SIP needed for remaining amount
      const monthlySip = remainingGoal > 0 
        ? remainingGoal / ((Math.pow(1 + rate, months) - 1) / rate * (1 + rate))
        : 0;
      
      // Lumpsum needed if no SIP (without initial lumpsum)
      const lumpsumNeeded = goalAmount / Math.pow(1 + annualRate, years);
      
      // Additional lumpsum needed (if current lumpsum is not enough)
      const additionalLumpsumNeeded = Math.max(0, lumpsumNeeded - initialLumpsum);
      
      setResult({
        monthly_sip_needed: monthlySip,
        lumpsum_needed: lumpsumNeeded,
        goal_amount: goalAmount,
        years: years,
        initial_lumpsum: initialLumpsum,
        lumpsum_growth: lumpsumGrowth,
        remaining_goal: remainingGoal,
        additional_lumpsum_needed: additionalLumpsumNeeded
      });
    }
  };

  useEffect(() => {
    calculate();
  }, [goalAmount, years, expectedReturn, initialLumpsum]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="goal-calculator">
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Target className="w-6 h-6 text-primary" />
            Goal Planning Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Goal Amount</Label>
              <div className="flex items-center gap-1 bg-secondary rounded-lg px-3 py-1">
                <IndianRupee className="w-4 h-4" />
                <Input
                  type="number"
                  value={goalAmount}
                  onChange={(e) => setGoalAmount(Number(e.target.value))}
                  className="w-28 bg-transparent border-0 p-0 text-right font-mono"
                  data-testid="goal-amount-input"
                />
              </div>
            </div>
            <Slider
              value={[goalAmount]}
              onValueChange={([v]) => setGoalAmount(v)}
              min={100000}
              max={100000000}
              step={100000}
              className="[&_[role=slider]]:bg-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>₹1 Lakh</span>
              <span>₹10 Crore</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Initial Lumpsum Investment</Label>
              <div className="flex items-center gap-1 bg-secondary rounded-lg px-3 py-1">
                <IndianRupee className="w-4 h-4" />
                <Input
                  type="number"
                  value={initialLumpsum}
                  onChange={(e) => setInitialLumpsum(Number(e.target.value))}
                  className="w-28 bg-transparent border-0 p-0 text-right font-mono"
                  data-testid="goal-lumpsum-input"
                />
              </div>
            </div>
            <Slider
              value={[initialLumpsum]}
              onValueChange={([v]) => setInitialLumpsum(v)}
              min={0}
              max={Math.min(goalAmount, 50000000)}
              step={10000}
              className="[&_[role=slider]]:bg-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>₹0</span>
              <span>{formatCurrency(Math.min(goalAmount, 50000000))}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Time to Goal</Label>
              <span className="bg-secondary rounded-lg px-3 py-1 font-mono">{years} years</span>
            </div>
            <Slider
              value={[years]}
              onValueChange={([v]) => setYears(v)}
              min={1}
              max={30}
              step={1}
              className="[&_[role=slider]]:bg-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 year</span>
              <span>30 years</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Expected Return (p.a.)</Label>
              <span className="bg-secondary rounded-lg px-3 py-1 font-mono">{expectedReturn}%</span>
            </div>
            <Slider
              value={[expectedReturn]}
              onValueChange={([v]) => setExpectedReturn(v)}
              min={1}
              max={30}
              step={0.5}
              className="[&_[role=slider]]:bg-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1%</span>
              <span>30%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="font-serif text-xl">Investment Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {result && (
            <div className="space-y-6">
              <div className="text-center p-6 bg-primary/10 rounded-xl border border-primary/20">
                <p className="text-muted-foreground mb-2">Your Goal</p>
                <p className="font-mono text-3xl font-bold text-primary">{formatCurrency(result.goal_amount)}</p>
                <p className="text-sm text-muted-foreground mt-1">in {result.years} years</p>
              </div>

              {initialLumpsum > 0 && (
                <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Your Lumpsum</span>
                    <span className="font-mono font-semibold">{formatCurrency(initialLumpsum)}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Will grow to</span>
                    <span className="font-mono font-semibold text-blue-400">{formatCurrency(result.lumpsum_growth)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Remaining to achieve via SIP</span>
                    <span className="font-mono font-semibold text-amber-400">{formatCurrency(result.remaining_goal)}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-secondary/30 border-border">
                  <CardContent className="p-6 text-center">
                    <PiggyBank className="w-8 h-8 text-green-500 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-1">
                      {initialLumpsum > 0 ? "Additional Monthly SIP" : "Monthly SIP Needed"}
                    </p>
                    <p className="font-mono text-xl font-bold" data-testid="goal-sip-needed">
                      {formatCurrency(result.monthly_sip_needed)}
                    </p>
                    {initialLumpsum > 0 && result.monthly_sip_needed === 0 && (
                      <p className="text-xs text-green-500 mt-2">Goal achieved with lumpsum alone!</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-secondary/30 border-border">
                  <CardContent className="p-6 text-center">
                    <Wallet className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-1">
                      {initialLumpsum > 0 ? "Or Additional Lumpsum" : "Lumpsum Needed"}
                    </p>
                    <p className="font-mono text-xl font-bold" data-testid="goal-lumpsum-needed">
                      {formatCurrency(initialLumpsum > 0 ? result.additional_lumpsum_needed : result.lumpsum_needed)}
                    </p>
                    {initialLumpsum > 0 && result.additional_lumpsum_needed === 0 && (
                      <p className="text-xs text-green-500 mt-2">No additional lumpsum needed!</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                * These calculations are based on assumed rate of return. Actual returns may vary.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
